import React from 'react';
import toast from 'react-hot-toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import { Modal, ModalTitle, MODAL_OUTER_PADDING, MODAL_INNER_GAP } from 'design-system/src/components/Modal';
import Hairline from 'design-system/src/components/Hairline/Hairline.tsx';
import { Patient } from '../../api.tsx';

import { useNavigate, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import { TypeaheadOption } from 'design-system/src/components/Typeahead/Typeahead';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { InboundTaskType } from '../../api.tsx';
import { ExtraInfoTable } from 'design-system/src/components/ExtraInfoTable/ExtraInfoTable';
import ExtendedNewTaskForm from './ExtendedNewTaskForm.tsx';

export type TaskFormData = {
  text: string;
  patientId: string;
  prescriptionId: string;
  type?: InboundTaskType;
};

export type RxData = {
  drug: string;
  drug_name: string;
  id: string;
  prescriber_first_name: string;
  prescriber_last_name: string;
  written_date: string;
};

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${MODAL_INNER_GAP};
`;

// custom ModalBody because the footer buttons are inside the form and can't easily be split out into the ModalFooter component
export const CustomModalBody = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex-grow: 1;
  padding: ${MODAL_INNER_GAP} ${MODAL_OUTER_PADDING} ${MODAL_OUTER_PADDING};
  gap: ${MODAL_INNER_GAP};
`;

export const StyledExtraInfoTable = styled(ExtraInfoTable)`
  // the FormContainer gap provides the spacing we need
  margin: 0;
`;

export const StyledHairline = styled(Hairline)`
  margin-top: 16px;
  // the FormContainer gap provides the bottom spacing we need
  margin-bottom: 0;
`;

export const LoadingForm = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

// Using <a> instead of NavLink because the Toast exists outside
// of the router system
// TODO(design-system): Replace with Link component
export const SuccessLink = styled.a`
  color: var(--green-800);
  font-weight: 500;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`;

// TODO(WIT-4362): fix
// eslint-disable-next-line react-refresh/only-export-components
export const createPatientOption = (patient: Patient): TypeaheadOption => {
  const option: TypeaheadOption = {
    value: patient.id,
    description: `${patient.first_name} ${patient.last_name} (${formatStandardDate(patient.date_of_birth)})`,
  };
  return option;
};

// TODO(WIT-4362): fix
// eslint-disable-next-line react-refresh/only-export-components
export const createRxOption = (
  rxData: RxData,
): {
  description: string;
  value: string;
} => {
  const { id, drug, prescriber_first_name, prescriber_last_name, written_date } = rxData;
  const date = formatStandardDate(written_date);

  const option: {
    description: string;
    value: string;
  } = {
    value: id,
    description: `${drug}, ${prescriber_first_name} ${prescriber_last_name} on ${date}`,
  };

  return option;
};

// TODO(WIT-4362): fix
// eslint-disable-next-line react-refresh/only-export-components
export const basicTaskTypeOptions: { value: string; label: InboundTaskType }[] = [
  { value: 'authorization_update', label: 'Authorization update' },
  { value: 'pap_bridge_update', label: 'PAP/Bridge update' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'pa_renewal', label: 'PA renewal' },
  { value: 'other', label: 'Other' },
];

export const NewTaskModal: React.FC = () => {
  const navigate = useNavigate();
  const outletContext: any = useOutletContext();

  return (
    <Modal onClose={() => navigate(-1)}>
      <ModalTitle>Create a new task</ModalTitle>
      <CustomModalBody>
        <ExtendedNewTaskForm
          onSubmit={(taskId: string) => {
            outletContext?.reload?.();
            toast.custom(() => <Toast variant="success">Task created.</Toast>);
            navigate(`/tasks/${taskId}`);
          }}
        />
      </CustomModalBody>
    </Modal>
  );
};

export default NewTaskModal;
